import { useDisableJourneyConversions } from 'modules/workflows/hooks/useDisableJourneyConversions';
import { useSelector, useDispatch } from 'react-redux';
import { getJourneyCurrentConversion, openModal } from 'modules/workflows/ducks';
import { useFormContext, useFormState } from 'react-hook-form';
import { GhostButton } from 'ui/button';
import { CLEAR_JOURNEY_CONVERSIONS_MODAL } from 'modules/workflows/constants/modals';
import { Spinner } from 'ui/spinner/Spinner';
import { useExitRuleContext } from 'modules/workflows/context/ExitRuleContext';
import { Link } from 'ui/links';
import { selectIsJourneySettingsRefactorFeatureFlagOn } from 'core/userContext';
import { JourneyConversions, defaultConversionValues } from '../../JourneyConversions';
import { HeaderWrapper, Header, Description } from '../SettingsTakeover.styles';
import {
  JourneyConversionsContainer,
  JourneyConversionsBodyWrapper,
  LoadingWrapper,
} from './Conversions.styled';

type ConversionsProps = { isDisabled: boolean };

export const ConversionsOld = ({ isDisabled }: ConversionsProps) => {
  const dispatch = useDispatch();
  const { reset, getValues } = useFormContext();
  const { getExitRules } = useExitRuleContext();
  const currentConversion = useSelector(getJourneyCurrentConversion);

  const currentConversionId = currentConversion?.id;
  const currentConversionEvent = currentConversion?.conversions?.[0]?.eventName;
  const { disableJourneyConversions, isLoading } = useDisableJourneyConversions();

  const openClearJourneyConversionsModal = () => {
    dispatch(
      openModal(CLEAR_JOURNEY_CONVERSIONS_MODAL, {
        conversionEvent: currentConversionEvent,
        onSave: () =>
          disableJourneyConversions({
            conversionIds: currentConversionId ? [currentConversionId] : [],
            showSuccessToast: false,
            onSuccess: () => {
              getExitRules();
              reset(
                {
                  ...getValues(),
                  conversions: defaultConversionValues,
                },
                {
                  keepDirtyValues: true,
                  keepDirty: true,
                },
              );
            },
          }),
      }),
    );
  };

  return (
    <JourneyConversionsContainer>
      <HeaderWrapper>
        <Header>Journey conversion</Header>
        <Description>
          <span>
            Track your journey&apos;s success by adding a conversion event for the journey and its
            associated campaigns.{' '}
            <Link
              hasOpenNewTabIcon
              target="_blank"
              href="https://support.iterable.com/hc/en-us/articles/10044063369492-Viewing-Journey-Engagement-Metrics"
              rel="noreferrer noopener"
              data-test="open-conversion-link"
            >
              Learn more
            </Link>
          </span>
        </Description>
      </HeaderWrapper>
      <JourneyConversionsBodyWrapper>
        {isLoading ? (
          <LoadingWrapper>
            <Spinner />
          </LoadingWrapper>
        ) : (
          <>
            <JourneyConversions isDisabled={isDisabled} showInfo={true} />
            <>
              {currentConversionId && (
                <GhostButton onClick={openClearJourneyConversionsModal}>Clear</GhostButton>
              )}
            </>
          </>
        )}
      </JourneyConversionsBodyWrapper>
    </JourneyConversionsContainer>
  );
};

export const ConversionsNew = ({ isDisabled }: ConversionsProps) => {
  const dispatch = useDispatch();
  const { dirtyFields } = useFormState();
  const { reset, getValues } = useFormContext();

  const { getExitRules } = useExitRuleContext();
  const currentConversion = useSelector(getJourneyCurrentConversion);

  const currentConversionId = currentConversion?.id;
  const currentConversionEvent = currentConversion?.conversions?.[0]?.eventName;
  const { disableJourneyConversions, isLoading } = useDisableJourneyConversions();

  const openClearJourneyConversionsModal = () => {
    dispatch(
      openModal(CLEAR_JOURNEY_CONVERSIONS_MODAL, {
        conversionEvent: currentConversionEvent,
        onSave: () =>
          disableJourneyConversions({
            conversionIds: currentConversionId ? [currentConversionId] : [],
            showSuccessToast: false,
            onSuccess: () => {
              getExitRules();

              // after we remove the conversion, we want to reset to the default values on the
              // form but we want to keep the dirty values for the metrics fields
              reset(
                {
                  ...getValues(),
                  conversions: defaultConversionValues,
                },
                {
                  keepDirtyValues: dirtyFields.metrics,
                  keepDirty: dirtyFields.metrics,
                },
              );
            },
          }),
      }),
    );
  };

  return (
    <JourneyConversionsContainer>
      <HeaderWrapper>
        <Header>Journey conversion</Header>
        <Description>
          <span>
            Track your journey&apos;s success by adding a conversion event for the journey and its
            associated campaigns.{' '}
            <Link
              hasOpenNewTabIcon
              target="_blank"
              href="https://support.iterable.com/hc/en-us/articles/10044063369492-Viewing-Journey-Engagement-Metrics"
              rel="noreferrer noopener"
              data-test="open-conversion-link"
            >
              Learn more
            </Link>
          </span>
        </Description>
      </HeaderWrapper>
      <JourneyConversionsBodyWrapper>
        {isLoading ? (
          <LoadingWrapper>
            <Spinner />
          </LoadingWrapper>
        ) : (
          <>
            <JourneyConversions isDisabled={isDisabled} showInfo={true} />
            <>
              {currentConversionId && (
                <GhostButton onClick={openClearJourneyConversionsModal}>Clear</GhostButton>
              )}
            </>
          </>
        )}
      </JourneyConversionsBodyWrapper>
    </JourneyConversionsContainer>
  );
};

export const Conversions = (props: ConversionsProps) => {
  const isJourneySettingsFeatureFlagEnabled = useSelector(
    selectIsJourneySettingsRefactorFeatureFlagOn,
  );
  const ConversionsFn = isJourneySettingsFeatureFlagEnabled ? ConversionsNew : ConversionsOld;
  return <ConversionsFn {...props} />;
};
